/* ==========================================================================
   SYNERGY COLLECTION PAGE — supporting custom elements
   - synergy-facets-ui : wraps the sidebar; stops UI-only inputs (search box,
                         "see all" toggles) from triggering the theme's
                         facets-form auto submit, and powers the brand search.
   - the wishlist heart lives in synergy-wishlist.js (loaded site-wide).
   - synergy-carousel  : prev/next scroll buttons for chips + "You may also like".
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- Applying ---------- */

  /* This file lives inside the collection section, and the theme re-runs it
     every time it swaps the section in after a filter change. Custom element
     definitions guard themselves; document-level listeners do not, so without
     this flag a second copy binds on every filter, and the duplicates then
     submit the same form twice and race each other -- one re-applying what the
     other just removed. */
  if (!window.__synergyCollectionBound) {
    window.__synergyCollectionBound = true;

  /* Every filter applies the moment it is touched, and the Apply button applies
     whatever is set -- two triggers on the same state, not two paths.

     facets-form marks itself dirty from the same change event, so submitting
     here is picked up rather than ignored. UI-only controls (the brand search
     box, the see-all toggles) carry data-ui and synergy-facets-ui stops their
     change events before they reach this listener. */
  function submitFacets(form) {
    if (!form) return;
    if (form.requestSubmit) form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  document.addEventListener('change', (event) => {
    const input = event.target;
    // facets-form, not .syn-sidebar: the mobile drawer is a second copy of the
    // same filters outside the sidebar, and it needs to apply on touch too.
    if (!input || !input.name || !input.closest('facets-form')) return;
    submitFacets(input.closest('form'));
  });

  /* Removing an applied filter from its chip. The checkbox is unticked first so
     the form and the chips agree: pressing Apply afterwards keeps it removed
     instead of reapplying it. The chip's own URL is the fallback for a value
     whose checkbox is not on the page. */
  /* Fixed price bands. They are not facet values -- Shopify's price filter is a
     single range -- so ticking one writes that range into the min/max boxes and
     submits. Several ticked span from the lowest floor to the highest ceiling,
     which is the closest a single range gets to "these bands". */
  document.addEventListener('change', (event) => {
    const band = event.target.closest('[data-syn-price-band]');
    if (!band) return;

    const form = band.closest('form');
    if (!form) return;
    const list = band.closest('.syn-price-bands');
    const ticked = Array.from(list.querySelectorAll('[data-syn-price-band]')).filter((b) => b.checked);

    const min = form.querySelector('input[name="filter.v.price.gte"]');
    const max = form.querySelector('input[name="filter.v.price.lte"]');

    if (!ticked.length) {
      if (min) min.value = '';
      if (max) max.value = '';
    } else {
      const floors = ticked.map((b) => Number(b.getAttribute('data-min')));
      const ceilings = ticked.map((b) => b.getAttribute('data-max'));
      if (min) min.value = String(Math.min(...floors));
      // An open-ended band ("$300+") means no ceiling at all.
      if (max) max.value = ceilings.some((c) => !c) ? '' : String(Math.max(...ceilings.map(Number)));
    }

    /* Announce the change from the price input rather than submitting here.
       facets-form only acts on a submit once it has seen a change, and the
       band's own event never reaches it -- so a direct submit is discarded.
       This one bubbles from a real filter input, which marks the form dirty
       and trips the listener above that does the submitting. */
    const announce = min || max;
    if (!announce) { submitFacets(form); return; }

    /* Deferred deliberately. Dispatched inline it would fire while this very
       change event is still propagating, and the theme's own price-range
       element -- which listens on these inputs to clamp them -- swallows it.
       On its own tick it behaves like any other edit to the price boxes. */
    window.setTimeout(() => announce.dispatchEvent(new Event('change', { bubbles: true })), 0);
  }, true);   // capture: the bands carry data-ui, and synergy-facets-ui stops
              // those change events on their way up so facets-form ignores
              // them. Capture runs first, so this still sees them.

  /* Clear all, at the top of the sidebar: every filter off in one go. The sort
     is deliberately left alone -- it is not part of the filter area, and a
     shopper who picked "Price, low to high" did not ask for it to be undone.
     The href is the fallback if the form is not on the page. */
  document.addEventListener('click', (event) => {
    const clearAll = event.target.closest('[data-syn-facet-clear-all]');
    if (!clearAll) return;

    // The drawer has its own copy of the filters, so clear the one this link
    // belongs to rather than always reaching for the sidebar's.
    const form = clearAll.closest('facets-form')?.querySelector('form')
      || document.querySelector('.syn-sidebar facets-form form');
    if (!form) return;                       // follow the link instead
    event.preventDefault();

    let changed = false;
    form.querySelectorAll('input').forEach((input) => {
      if (input.hasAttribute('data-ui') || input.name === 'sort_by') return;
      if (input.type === 'checkbox' && input.checked) { input.checked = false; changed = true; }
      if ((input.type === 'number' || input.type === 'text') && input.value !== '') { input.value = ''; changed = true; }
    });

    if (!changed) return;
    form.dispatchEvent(new Event('change', { bubbles: true }));
    submitFacets(form);
  });

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-syn-facet-remove]');
    const clear = event.target.closest('[data-syn-facet-clear]');
    const chip = remove || clear;
    if (!chip) return;

    event.preventDefault();
    const form = chip.closest('form');
    const name = chip.getAttribute('data-name');
    let unticked = false;

    if (form && name) {
      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        if (input.name !== name || !input.checked) return;
        if (remove && input.value !== chip.getAttribute('data-value')) return;
        input.checked = false;
        unticked = true;
      });
    }

    if (unticked) {
      // Let facets-form see a change, so it treats the submit as meaningful.
      form.dispatchEvent(new Event('change', { bubbles: true }));
      submitFacets(form);
      return;
    }

    const url = chip.getAttribute('data-url');
    if (url) window.location.href = url;
  });

  }

  /* ---------- Facets UI helper ---------- */
  if (!window.customElements.get('synergy-facets-ui')) {
    class SynergyFacetsUi extends HTMLElement {
      constructor() {
        super();

        // UI-only controls are marked with data-ui; their change events must
        // not bubble to <facets-form>, which would trigger a page refetch.
        this.addEventListener('change', (event) => {
          if (event.target.hasAttribute('data-ui')) {
            event.stopPropagation();
          }
        });

        this.addEventListener('input', (event) => {
          if (event.target.matches('[data-ui][data-filter-search]')) {
            this._filterOptions(event.target);
          }
        });
      }

      _filterOptions(input) {
        const group = input.closest('.syn-filter-group__content');
        if (!group) return;

        const query = input.value.trim().toLowerCase();
        const list = group.querySelector('.syn-option-list');
        if (!list) return;

        // While searching, disable the "first 8 only" collapse so matches
        // beyond the fold are visible.
        list.classList.toggle('syn-option-list--collapsible', query === '' && list.hasAttribute('data-collapsible'));

        const seeAll = group.querySelector('.syn-see-all');
        if (seeAll) seeAll.style.display = query === '' ? '' : 'none';

        list.querySelectorAll('.syn-option').forEach((option) => {
          const label = (option.getAttribute('data-label') || option.textContent).toLowerCase();
          option.hidden = query !== '' && !label.includes(query);
        });
      }
    }

    window.customElements.define('synergy-facets-ui', SynergyFacetsUi);
  }

  /* ---------- Wishlist ----------
     The heart element and its storage now live in assets/synergy-wishlist.js,
     which loads on every page so the header count stays in step. Defining it
     here as well would mean whichever file loaded first won. */

  /* ---------- Carousel ---------- */
  if (!window.customElements.get('synergy-carousel')) {
    class SynergyCarousel extends HTMLElement {
      connectedCallback() {
        this._track = this.querySelector('[data-track]');
        this._prev = this.querySelector('[data-prev]');
        this._next = this.querySelector('[data-next]');
        if (!this._track) return;

        this._prev?.addEventListener('click', () => this._scrollBy(-1));
        this._next?.addEventListener('click', () => this._scrollBy(1));
        this._track.addEventListener('scroll', () => this._updateArrows(), { passive: true });
        window.addEventListener('resize', () => this._updateArrows(), { passive: true });
        this._updateArrows();
      }

      _scrollBy(direction) {
        this._track.scrollBy({ left: direction * this._track.clientWidth * 0.8, behavior: 'smooth' });
      }

      _updateArrows() {
        if (!this._prev && !this._next) return;

        /* A track at rest does not sit at exactly 0: sub-pixel column widths
           leave scrollLeft at a pixel or two, which was enough for the "are we
           at the start" test to fail and leave a back arrow on the first page
           with nothing behind it. Both ends get the same tolerance. */
        const EDGE = 4;
        const maxScroll = this._track.scrollWidth - this._track.clientWidth;
        if (this._prev) this._prev.toggleAttribute('disabled', this._track.scrollLeft <= EDGE);
        if (this._next) this._next.toggleAttribute('disabled', this._track.scrollLeft >= maxScroll - EDGE);
      }
    }

    window.customElements.define('synergy-carousel', SynergyCarousel);
  }

  /* ---------- Size sheet ---------- */
  if (!window.customElements.get('synergy-size-sheet')) {
    class SynergySizeSheet extends HTMLElement {
      connectedCallback() {
        this._panel = this.querySelector('.syn-sheet__panel');
        this._variantInput = this.querySelector('[data-syn-variant-input]');
        this._form = this.querySelector('form');

        // Opener lives outside this element, next to it on the card.
        const card = this.closest('product-card') || document;
        this._opener = card.querySelector('[aria-controls="' + CSS.escape(this.id) + '"]');
        this._opener?.addEventListener('click', () => this.open());

        this.querySelectorAll('[data-syn-sheet-close]').forEach((el) =>
          el.addEventListener('click', () => this.close()));

        // Radio choice drives the hidden id the theme's product form submits.
        this.addEventListener('change', (event) => {
          if (event.target.matches('input[type="radio"]')) {
            this._variantInput.value = event.target.value;
          }
        });

        // The theme dispatches variant:add once the item is really in the cart.
        this._form?.addEventListener('variant:add', () => this.close());

        this._onKey = (event) => { if (event.key === 'Escape') this.close(); };
      }

      open() {
        this.hidden = false;
        // Next frame, so the transition runs from the off-screen position.
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          const checked = this.querySelector('input[type="radio"]:checked:not(:disabled)')
            || this.querySelector('input[type="radio"]:not(:disabled)');
          if (checked) { checked.checked = true; this._variantInput.value = checked.value; }
          (checked || this.querySelector('.syn-sheet__close'))?.focus({ preventScroll: true });
        });
        document.addEventListener('keydown', this._onKey);
      }

      close() {
        this.classList.remove('is-open');
        document.removeEventListener('keydown', this._onKey);
        const done = () => { this.hidden = true; };
        // Wait out the slide-down, but never leave it stuck open.
        this._panel?.addEventListener('transitionend', done, { once: true });
        window.setTimeout(done, 400);
        this._opener?.focus({ preventScroll: true });
      }
    }

    window.customElements.define('synergy-size-sheet', SynergySizeSheet);
  }

  /* ---------- Breadcrumb back link ---------- */
  if (!window.__synBackBound) {
    window.__synBackBound = true;
    document.addEventListener('click', (event) => {
      const back = event.target.closest('[data-syn-back]');
      if (!back) return;
      if (window.history.length > 1 && document.referrer && document.referrer.indexOf(window.location.host) !== -1) {
        event.preventDefault();
        window.history.back();
      }
      // Otherwise fall through to the link's href (home).
    });
  }
})();
